import React, { useState, useEffect, useCallback } from 'react';
import './Browse.css'; // Import the CSS file for styling
import { useLocation, Link } from 'react-router-dom';
import SearchBar from './SearchBar'; // Import the shared SearchBar component

function Browse() {
    console.log('=== Browse Component Start ===');
    console.log('Component mounting...');

    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        surfaceCoverage: null,
        cellLines: [],
        annotation: []
    });
    const [proteins, setProteins] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [prevPageUrl, setPrevPageUrl] = useState(null);

    const location = useLocation();
    const apiUrl = process.env.REACT_APP_API_URL || 'https://cellsurfacemap-backend.onrender.com';

    console.log('Configuration:', {
        apiUrl,
        currentPath: location.pathname,
        searchParams: location.search
    });

    const fetchProteins = useCallback(async (query = '', pageUrl = '') => {
        setIsLoading(true);
        
        try {
            let url = pageUrl;
            if (!url) {
                url = `${apiUrl}/api/proteins/?query=${query}`;
                if (filters.surfaceCoverage) {
                    url += `&surface_coverage=${filters.surfaceCoverage}`;
                }
                if (filters.cellLines.length > 0) {
                    filters.cellLines.forEach(line => {
                        url += `&cell_lines[]=${line}`;
                    });
                }
                if (filters.annotation.length > 0) {
                    filters.annotation.forEach(anno => {
                        url += `&annotation[]=${encodeURIComponent(anno)}`;
                    });
                }
            }

            console.log('=== Fetching Proteins ===');
            console.log('URL:', url);
            console.log('Current filters:', filters);

            const response = await fetch(url);
            const data = await response.json();
            
            // Set data directly from backend
            setProteins(data.results);
            setNextPageUrl(data.next);
            setPrevPageUrl(data.previous);
        } catch (error) {
            console.error('Fetch error:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [apiUrl, filters]);

    // Single effect to handle both URL params and filter changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchParam = params.get('search');
        
        if (searchParam && searchParam !== searchTerm) {
            // Update searchTerm if URL param changes
            setSearchTerm(searchParam);
        } else {
            // Use current searchTerm for fetching
            fetchProteins(searchTerm);
        }
    }, [location.search, searchTerm, filters, fetchProteins]);

    // Update filter change handler to trigger new fetch
    const handleFilterChange = (filterType, value) => {
        setFilters(prevFilters => {
            const newFilters = {
                ...prevFilters,
                [filterType]: value
            };
            return newFilters;
        });
    };

    return (
        <div className="browse-page">
            <div className="header-search-container">
                <h1 className="text-3xl font-serif mb-4">Browse Data...</h1>
                <SearchBar className="browse-page-search-bar items-center w-full max-w-[1000px]" />
            </div>
            
            {isLoading && (
                <div className="text-center py-4">
                    Loading proteins...
                </div>
            )}
            
            {error && (
                <div className="text-center py-4 text-red-600">
                    Error: {error}
                </div>
            )}
            
            {!isLoading && !error && proteins.length === 0 && (
                <div className="text-center py-4">
                    No proteins found.
                </div>
            )}

            <div className="browse-container">
                <div className="filter-section">
                    <h2>Filter By:</h2>
                    
                    {/* Surface Coverage Filter */}
                    <div className="filter-group">
                        <label>Graph Connectivity</label>
                        <select 
                            onChange={(e) => handleFilterChange('surfaceCoverage', e.target.value)}
                            className="w-full p-2 border rounded"
                        >
                            <option value="">All</option>
                            <option value="lowToHigh">Low to High</option>
                            <option value="highToLow">High to Low</option>
                        </select>
                    </div>

                    {/* Cell Lines Filter */}
                    <div className="filter-group">
                        <label>Cell Lines</label>
                        <div className="flex flex-col gap-2">
                            {['Jurkat', 'Daudi'].map(cellLine => (
                                <label key={cellLine} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        value={cellLine}
                                        checked={filters.cellLines.includes(cellLine)}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleFilterChange('cellLines', 
                                                e.target.checked
                                                    ? [...filters.cellLines, value]
                                                    : filters.cellLines.filter(cl => cl !== value)
                                            );
                                        }}
                                    />
                                    {cellLine}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Functional Annotation Filter */}
                    <div className="filter-group mt-4">
                        <label>Functional Annotation</label>
                        <div className="flex flex-col gap-2">
                            {[
                                'Adaptive Immunity',
                                'General Membrane',
                                'Maverick',
                                'Receptor',
                                'Cell Adhesion',
                                'Secreted',
                                'Extracellular Exosome'
                            ].map(annotation => (
                                <label key={annotation} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        value={annotation}
                                        checked={filters.annotation.includes(annotation)}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleFilterChange('annotation', 
                                                e.target.checked
                                                    ? [...filters.annotation, value]
                                                    : filters.annotation.filter(a => a !== value)
                                            );
                                        }}
                                    />
                                    {annotation}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="data-cards">
                    {proteins.length > 0 ? (
                        proteins.map(protein => (
                            <div key={protein.id} className="data-card">
                                <Link to={`/proteins/${protein.id}`}>
                                    <h3>{protein.gene_name}</h3>
                                </Link>
                                <p><strong>Protein Name:</strong> {protein.protein_name}</p>
                                <p><strong>UniProt ID:</strong> {protein.uniprot_id}</p>
                                <p><strong>Cell Lines:</strong> {protein.cell_lines || 'N/A'}</p>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4">
                            No proteins match the selected filters.
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination buttons */}
            <div className="flex justify-center gap-8 mt-8 mb-8">
                <button
                    onClick={() => prevPageUrl && fetchProteins(searchTerm, prevPageUrl)}
                    disabled={!prevPageUrl}
                    className={`px-4 py-2 rounded text-[#9b4819] ${
                        !prevPageUrl ? 'text-gray-300 cursor-not-allowed' : 'hover:text-[#7b3813]'
                    }`}
                >
                    Previous
                </button>
                <button
                    onClick={() => nextPageUrl && fetchProteins(searchTerm, nextPageUrl)}
                    disabled={!nextPageUrl}
                    className={`px-4 py-2 rounded ${
                        !nextPageUrl ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#9b4819] text-white hover:bg-[#7b3813]'
                    }`}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default Browse;

